import "./Takeaway.scss";

import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import api_key from "../../../env";
import { getOrganization, getOrgProducts, getOrgCategories } from "../../../utility";
import { addItemToCart, removeItemFromCart } from "../../../redux/cart/reducer";
import {
  canAddStockManagedProduct,
  filterSellableProducts,
} from "../../../utility/stockManagement";
import SocialNetworkds from "../../../components/socialNetworks/SocialNetworks";
import Banner from "../../../components/Banner";
import Header from "../../../components/Header";
import TakeawayCartModal from "../takeawayModal/takeawayCartModal/TakeawayCartModal";
import TakeawayCareModal from "../takeawayModal/takewayCareModal/TakeawayCareModal";
import TakeawayPaymentModal from "../takeawayModal/takewayPaymentModal/TakeawayPaymentModal";
import LoadingSpinner from "../../../components/spinners/DrinkSpinner";
import Footer from "../../../components/Footer";
import NoItemsMissingModal from "../takeawayModal/noItemsMissingModal/NoItemsMissingModal";
import Schedule from "../../../components/schedule/Schedule";
import { Loader2 } from "../../../helper/loader";
import Closed from "../../../components/lotties/closed.json";
import Loading from "../../../components/lotties/loading.json";
import { AllIcon } from "../../../components/Icon";
import Lottie from "lottie-react";
import {
  BeersArray,
  CocktailsArray,
  FoodArray,
  HotDrinksArray,
  OtherArray,
  SoftsArray,
  WineArray,
  CategoriesArray,
} from "../../../utility/data";
import { Icons } from "../../../images/icons";
import DefaultCategoryImage from "../../../images/Beers.svg";

const Takeaway = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("takeaway");

  const filterMenuRef = useRef();
  const bottomRef = useRef();
  const [filterMenuVisible, setFilterMenuVisible] = useState(true);
  const [bottomVisible, setBottomVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [careModalOpen, setCareModalOpen] = useState(false);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState("...");
  const [org, setOrg] = useState({});
  const [filteredProducts, setFilteredProducts] = useState([]);
  const products = useRef([]);
  const navigate = useNavigate();
  const { orgId } = useParams();
  const cartStore = useSelector((state) => {
    const cartByOrg = state?.cartReducer?.cartByOrg;
    if (cartByOrg && cartByOrg[orgId]) {
      return cartByOrg[orgId];
    }
    return [];
  });

  const shouldShowFloatingCart = !filterMenuVisible && !bottomVisible;
  const alert = useAlert();
  const [categories, setCategories] = useState([""]);
  const [total, setTotal] = useState(0);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [noMissingItems, setNoMissingItems] = useState([]);

  useEffect(() => {
    const filterMenuObserver = new IntersectionObserver(
      ([entry]) => {
        setFilterMenuVisible(entry.isIntersecting);
      },
      { threshold: 0.7 },
    );

    const bottomObserver = new IntersectionObserver(([entry]) => {
      setBottomVisible(entry.isIntersecting);
    });

    if (filterMenuRef.current) {
      filterMenuObserver.observe(filterMenuRef.current);
    }
    if (bottomRef.current) {
      bottomObserver.observe(bottomRef.current);
    }

    return () => {
      filterMenuObserver.disconnect();
      bottomObserver.disconnect();
    };
  }, []);

  const getOrganizationHandler = async () => {
    setLoading(true);
    try {
      const orgData = await getOrganization(orgId);
      validateOrganization(orgData);

      const loadedProducts = await getOrgProducts(orgId);
      const availableProducts = filterAvailableProducts(loadedProducts);
      products.current = availableProducts;

      const sortedProducts = sortProductsByPrice(availableProducts);
      setFilteredProducts(sortedProducts);

      const hardcodedCategories = getHardcodedCategories();
      const definedCategories = getDefinedCategories(sortedProducts, hardcodedCategories);

      const fetchedCategories = await fetchCategories(orgId);
      const mappedFetchedCategories = mapFetchedCategories(fetchedCategories);

      const allCategories = [...definedCategories, ...(mappedFetchedCategories || [])];
      const filteredCategories = filterCategories(allCategories, sortedProducts);

      setCategories(filteredCategories);
      setOrg(orgData.org);
      setOrgName(orgData.org.organization);
      setTotal(calculateTotal(cartStore));
    } catch (err) {
      console.error("Error in getOrganizationHandler:", err);
      alert.show(t("error_generic"), {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper Functions
  const validateOrganization = (orgData) => {
    if (!orgData.org.stripeId || !orgData.org.stripeActivated || !("takeawayOpen" in orgData.org)) {
      alert.show(t("error_inactive_cash_register"), { type: "error" });
      navigate("/");
      throw new Error("Invalid organization");
    }
  };

  const filterAvailableProducts = (loadedProducts) => {
    return filterSellableProducts(loadedProducts?.products || []);
  };

  const sortProductsByPrice = (products) => {
    return products.sort((a, b) => Number(a.price) - Number(b.price));
  };

  const getHardcodedCategories = () => {
    return [
      { label: t("beers"), value: "Beers", icons: Icons.beer },
      { label: t("food"), value: "Food", icons: Icons.food },
      { label: t("softs"), value: "Softs", icons: Icons.soft },
      { label: t("cocktails"), value: "Cocktails", icons: Icons.cocktail },
      { label: t("wine"), value: "Wine", icons: Icons.wine },
      { label: t("hot_drinks"), value: "Hot Drinks", icons: Icons.hotDrink },
      { label: t("others"), value: "Other", icons: Icons.other },
      // Add more hardcoded categories as needed
    ];
  };

  const getDefinedCategories = (availableProducts, hardcodedCategories) => {
    const productsCategories = new Set(availableProducts.map((product) => product.category));
    return hardcodedCategories.filter((category) => productsCategories.has(category.value));
  };

  const fetchCategories = async (orgId) => {
    const fetchedCategories = await getOrgCategories(orgId);
    if (!fetchedCategories) {
      alert.show(t("error_fetch_categories"), {
        type: "error",
      });
      throw new Error("Failed to fetch categories");
    }
    return fetchedCategories.categories;
  };

  const mapFetchedCategories = (fetchedCategories) => {
    return fetchedCategories?.map((category) => ({
      label: category.title,
      value: category.title,
      icons: CategoriesArray.find((cat) => cat.name === category.customIcon)?.icon,
      hideFromFastpass: category.hideFromFastpass,
    }));
  };

  const filterCategories = (allCategories, availableProducts) => {
    const productsCategories = new Set(availableProducts.map((product) => product.category));

    return allCategories.filter(
      (category) =>
        (productsCategories.has(category.value) || productsCategories.has(category.label)) &&
        !category.hideFromFastpass,
    );
  };

  const calculateTotal = (cartStore) => {
    return cartStore?.reduce((total, item) => total + parseFloat(item?.totalDue || 0), 0) || 0;
  };

  useEffect(() => {
    getOrganizationHandler();
  }, [orgId]);

  useEffect(() => {
    setLoading(true);

    if (active === "all") {
      setFilteredProducts(products.current.sort((a, b) => Number(a.price) - Number(b.price)));
    } else {
      const activeProducts = products.current.filter((product) => product.category === active);
      setFilteredProducts(activeProducts.sort((a, b) => Number(a.price) - Number(b.price)));
    }
    setLoading(false);
  }, [active]);

  function handleCartOpen() {
    if (
      categories.find((category) => category.label === "Sauces" || category.label === "Suppléments")
    ) {
      const sauces = products.current.filter((item) => item.category === "Sauces");
      const supplements = products.current.filter((item) => item.category === "Suppléments");
      setNoMissingItems([...sauces, ...supplements]);
      setModalOpen(true);
    } else {
      if (total < 5) {
        setCareModalOpen(true);
      } else {
        setCartModalOpen(true);
      }
    }
  }

  function openGoogleMap() {
    const url = `https://www.google.com/maps?q=${org.location.latitude},${org.location.longitude}`;
    window.open(url, "_blank");
  }

  return (
    <>
      {loading && <Loader2 />}
      {!loading && (
        <>
          <Header />
          <Banner
            event={{
              name: orgName,
              imageUrl: org.coverUrl,
              description: org.takeawayDescription,
            }}
            takeaway
          />

          <section className="takeaway-content">
            {org.takeawayOpen && (
              <>
                <ul className="filter-menu" ref={filterMenuRef}>
                  <div className="categories-menu">
                    <li
                      key={0}
                      className={active === "all" ? "active" : ""}
                      onClick={() => setActive("all")}
                    >
                      <AllIcon />
                    </li>

                    {categories.map((item, index) => (
                      <li
                        key={index}
                        className={active === item.value || active === item.label ? "active" : ""}
                        onClick={() => setActive(item.value)}
                      >
                        <img
                          src={item?.icons || DefaultCategoryImage}
                          className={active === item.value ? "lit-icon" : ""}
                        />
                        {item.label || item.value}
                      </li>
                    ))}
                  </div>
                  <li key={categories.length} className={`cart`} onClick={handleCartOpen}>
                    <p>{`${t("cart")} : ${total.toFixed(2)}€`}</p>
                  </li>
                </ul>

                <div className="mt-5">
                  {orgName === t("loading") ? (
                    <LoadingSpinner height="40%" width="40%" />
                  ) : (
                    <div className="row g-4">
                      {filteredProducts.map((item, i) => (
                        <div className="col-6 col-sm-6 col-md-4 col-xl-4 col-xxl-3" key={i}>
                          <CaiseMenuBox
                            itemData={item}
                            cart={cartStore}
                            addToCart={(item) => {
                              if (!canAddStockManagedProduct(item, cartStore)) {
                                alert.show(t("maxQuantityReached"), { type: "error" });
                                return;
                              }
                              setTotal((prev) => prev + parseFloat(item.price));
                              dispatch(addItemToCart({ orgId, item }));
                            }}
                            removeFromCart={(item) => {
                              setTotal((prev) => prev - parseFloat(item.price));
                              dispatch(removeItemFromCart({ orgId, item }));
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {!org.id && (
              <div className="takeaway-closed">
                <Lottie animationData={Loading} className="takeaway-closed-lottie" />
              </div>
            )}
            {org.id && !org.takeawayOpen && (
              <div className="takeaway-closed">
                <Lottie animationData={Closed} className="takeaway-closed-lottie" />
              </div>
            )}
            <div className="__bottom_wrapper">
              <div className="__bottom_wrapper_top">
                <div className="__localization-form">
                  <h3 className="__localization-form-title">{t("localization")}</h3>

                  <div className="col-12">
                    <div className="__ticket-location">
                      <div className="__ticket-location-cont">
                        <div className="__ticket-location-innercont">
                          <span className="info">{t("your_address")}</span>
                          <span className="subtitle">
                            {org?.location?.address || t("address_placeholder")}
                          </span>
                        </div>
                        <div className="right-detail">
                          <div className="apply-promo-container">
                            <button onClick={openGoogleMap} className="applyPromo">
                              {t("show_on_map")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {org?.location?.latitude && org?.location?.longitude ? (
                      <div className="ticket-map">
                        <APIProvider apiKey={api_key.google_maps_api_key}>
                          <Map
                            defaultCenter={{
                              lat: org.location.latitude,
                              lng: org.location.longitude,
                            }}
                            defaultZoom={17}
                            gestureHandling="greedy"
                            disableDefaultUI={false}
                          >
                            <Marker
                              position={{
                                lat: org.location.latitude,
                                lng: org.location.longitude,
                              }}
                            />
                          </Map>
                        </APIProvider>
                      </div>
                    ) : (
                      <p>{t("loading_map")}</p>
                    )}
                  </div>
                </div>

                <Schedule schedule={org.schedule} />
              </div>

              <div className="__bottom_wrapper_bottom">
                <SocialNetworkds />

                <div className="__ticket-contact-details">
                  <div className="__ticket-contact-area">
                    <div className="__ticket-contact-area-container">
                      <div className="__ticket-contact-area-image">
                        <img src={org.logo} alt={t("logo")} />
                      </div>
                      <div className="__ticket-contact-area-title">
                        <div className="title">{org.organization}</div>
                        <div className="email">
                          {org.adminEmail !== null &&
                            org.adminEmail?.length > 0 &&
                            org.adminEmail[0]}
                        </div>
                      </div>
                    </div>
                    <div className="__ticket-contact-area-button">
                      {org.adminEmail && org.adminEmail.length > 0 ? (
                        <a style={{ textDecoration: "none" }} href={`mailto:${org.adminEmail[0]}`}>
                          <button className="contactBtn">{t("contact")}</button>
                        </a>
                      ) : (
                        <button className="contactBtn" disabled>
                          {t("no_contact_available")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <TakeawayCartModal
              open={cartModalOpen}
              setOpen={setCartModalOpen}
              total={total}
              setCareModalOpen={setCareModalOpen}
              setTotal={setTotal}
              setPaymentModalOpen={setPaymentModalOpen}
              orgId={orgId}
            />
            <TakeawayPaymentModal
              open={paymentModalOpen}
              setOpen={setPaymentModalOpen}
              sellerPaysFees={org?.sellerPaysFees}
            />
            <TakeawayCareModal open={careModalOpen} setOpen={setCareModalOpen} />
            <NoItemsMissingModal
              open={modalOpen}
              setOpen={setModalOpen}
              setCartModal={setCartModalOpen}
              setTotal={setTotal}
              total={total}
              items={noMissingItems}
              setCareModalOpen={setCareModalOpen}
            />
            {shouldShowFloatingCart && (
              <div className="floating-cart" onClick={handleCartOpen}>
                🛒 {total.toFixed(2)} €
              </div>
            )}
          </section>

          <div ref={bottomRef}>
            <Footer />
          </div>
        </>
      )}
    </>
  );
};

const CaiseMenuBox = ({ itemData, cart, addToCart, removeFromCart }) => {
  const [isInCart, setIsInCart] = useState(null);

  useEffect(() => {
    setIsInCart(cart?.find((item) => item.id === itemData?.id));
  }, [[cart, itemData]]);

  const iconArrays = {
    Food: { array: FoodArray, default: Icons.food },
    Softs: { array: SoftsArray, default: Icons.soft },
    Beers: { array: BeersArray, default: Icons.beer },
    Cocktails: { array: CocktailsArray, default: Icons.cocktail },
    Wine: { array: WineArray, default: Icons.wine },
    "Hot drinks": { array: HotDrinksArray, default: Icons.drink },
    Other: { array: OtherArray, default: Icons.others },
    default: { array: CategoriesArray, default: Icons.others },
  };

  const getImageSrc = (item) => {
    const category = item?.category;
    const iconData = iconArrays[category] || iconArrays.default;

    if (item?.imageUrl) {
      return item?.imageUrl;
    }
    if (item?.customIcon && item.customIcon !== "") {
      return iconData.array.find((i) => i.name === item.customIcon)?.icon || iconData.default;
    } else {
      return iconData.default;
    }
  };

  return (
    <>
      <div className="caise-menu-box">
        <div className="click" onClick={() => addToCart(itemData)} />
        {isInCart && (
          <>
            <div className="close-1" onClick={() => removeFromCart(itemData)}>
              {" "}
              X{" "}
            </div>
            <div className="close-2">x{isInCart?.quantity}</div>
          </>
        )}
        <div className={itemData?.imageUrl ? "imageWrapper" : "imageWrapper"}>
          <img src={getImageSrc(itemData)} alt="" />
        </div>
        <div className="details">
          <h6 className="name">{itemData?.title}</h6>
          <h6 className="price">{itemData?.price}€</h6>
        </div>
      </div>
    </>
  );
};

export default Takeaway;
